const stateKey = '__lngvtyCartMotivatorState';
const confettiKey = '__lngvtyCartConfetti';
const rewardSyncKey = '__lngvtyCartRewardSync';
const rewardReconcileTimerKey = '__lngvtyCartRewardReconcileTimer';
const rewardFailureKey = '__lngvtyCartRewardFailures';
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function normalizedMarker(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizedShopifyId(value) {
  return String(value || '').split('/').pop();
}

if (!globalThis[rewardFailureKey]) globalThis[rewardFailureKey] = new Set();

class LngvtyCartConfetti {
  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'lngvty-cart-confetti';
    this.canvas.setAttribute('aria-hidden', 'true');
    this.context = this.canvas.getContext('2d');
    this.particles = [];
    this.frame = 0;
    this.lastTime = 0;
    this.resize = this.resize.bind(this);
    this.render = this.render.bind(this);
    document.body.append(this.canvas);
    window.addEventListener('resize', this.resize, { passive: true });
    this.resize();
  }

  resize() {
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(window.innerWidth * ratio);
    this.canvas.height = Math.round(window.innerHeight * ratio);
    this.canvas.style.width = `${window.innerWidth}px`;
    this.canvas.style.height = `${window.innerHeight}px`;
    this.context?.setTransform(ratio, 0, 0, ratio, 0, 0);
  }

  burst(x, y, count = 70) {
    if (reduceMotion.matches || !this.context) return;

    const colors = ['#075a43', '#0b7658', '#8fc1a5', '#d8ede0', '#d9b45f', '#fff7de', '#ffffff'];
    for (let index = 0; index < count; index += 1) {
      const angle = -Math.PI * (0.12 + Math.random() * 0.76);
      const speed = 3.5 + Math.random() * 7.5;
      const life = 720 + Math.random() * 720;
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed * (0.72 + Math.random() * 0.55),
        vy: Math.sin(angle) * speed,
        gravity: 0.13 + Math.random() * 0.08,
        drag: 0.986 + Math.random() * 0.008,
        rotation: Math.random() * Math.PI,
        spin: (Math.random() - 0.5) * 0.34,
        size: 3 + Math.random() * 5,
        life,
        maxLife: life,
        color: colors[Math.floor(Math.random() * colors.length)],
        shape: Math.floor(Math.random() * 3),
      });
    }

    if (!this.frame) {
      this.lastTime = performance.now();
      this.frame = window.requestAnimationFrame(this.render);
    }
  }

  render(time) {
    const context = this.context;
    if (!context) return;

    const delta = Math.min((time - this.lastTime) / 16.67, 2);
    this.lastTime = time;
    context.clearRect(0, 0, window.innerWidth, window.innerHeight);
    this.particles = this.particles.filter((particle) => {
      particle.life -= 16.67 * delta;
      if (particle.life <= 0) return false;

      particle.vx *= particle.drag ** delta;
      particle.vy = particle.vy * particle.drag ** delta + particle.gravity * delta;
      particle.x += particle.vx * delta;
      particle.y += particle.vy * delta;
      particle.rotation += particle.spin * delta;

      const opacity = Math.min(1, particle.life / (particle.maxLife * 0.28));
      context.save();
      context.globalAlpha = opacity;
      context.translate(particle.x, particle.y);
      context.rotate(particle.rotation);
      context.fillStyle = particle.color;
      if (particle.shape === 0) {
        context.beginPath();
        context.arc(0, 0, particle.size * 0.55, 0, Math.PI * 2);
        context.fill();
      } else if (particle.shape === 1) {
        context.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size);
      } else {
        context.fillRect(-particle.size, -1.4, particle.size * 2, 2.8);
      }
      context.restore();
      return particle.y < window.innerHeight + 60;
    });

    if (this.particles.length) {
      this.frame = window.requestAnimationFrame(this.render);
    } else {
      context.clearRect(0, 0, window.innerWidth, window.innerHeight);
      this.frame = 0;
    }
  }
}

function getConfetti() {
  if (reduceMotion.matches) return null;
  if (!globalThis[confettiKey]) globalThis[confettiKey] = new LngvtyCartConfetti();
  return globalThis[confettiKey];
}

if (!globalThis[stateKey]) {
  const initialMotivator = document.querySelector('lngvty-cart-motivator');
  globalThis[stateKey] = initialMotivator
    ? {
        total: Number(initialMotivator.dataset.cartTotal || 0),
        progress: Number(initialMotivator.dataset.progress || 0),
        message: initialMotivator.querySelector('.lngvty-cart-motivator__message')?.textContent?.replace(/\s+/g, ' ').trim() || '',
      }
    : { total: 0, progress: 0, message: '' };
}

class LngvtyCartRewardManager extends HTMLElement {
  static get observedAttributes() {
    return [
      'data-cart-total',
      'data-subscription-bottle-present',
      'data-subscription-library-present',
    ];
  }

  #retryTimer;

  connectedCallback() {
    window.requestAnimationFrame(() => this.reconcile({ authoritative: true }));
  }

  disconnectedCallback() {
    window.clearTimeout(this.#retryTimer);
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (!this.isConnected || oldValue === null || oldValue === newValue) return;
    window.requestAnimationFrame(() => this.reconcile());
  }

  async reconcile({ authoritative = true } = {}) {
    if (globalThis[rewardSyncKey]) {
      window.clearTimeout(this.#retryTimer);
      this.#retryTimer = window.setTimeout(() => {
        if (this.isConnected) this.reconcile({ authoritative });
      }, 120);
      return;
    }

    globalThis[rewardSyncKey] = true;

    try {
      let qualifyingTotal = Number(this.dataset.cartTotal || 0);
      let subscriptionBottlePresent = this.dataset.subscriptionBottlePresent === 'true';
      let subscriptionLibraryPresent = this.dataset.subscriptionLibraryPresent === 'true';
      const authoritativeRewardLines = new Map();
      const authoritativeUnmarkedProductLines = new Map();
      const authoritativeUnmarkedVariantIds = new Set();
      const updates = {};
      const additions = [];

      if (authoritative) {
        const stateResponse = await fetch('/cart.js', {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (!stateResponse.ok) throw new Error('Unable to verify cart rewards');

        const cartState = await stateResponse.json();
        qualifyingTotal = 0;
        subscriptionBottlePresent = false;
        subscriptionLibraryPresent = false;
        const subscriptionBottleProductId = Number(this.dataset.subscriptionBottleProductId || 0);
        const subscriptionLibraryProductId = Number(this.dataset.subscriptionLibraryProductId || 0);
        const cartItems = cartState.items || [];
        const rewardConfigs = new Map(
          Array.from(this.querySelectorAll('[data-reward-id]'))
            .filter((element) => element instanceof HTMLElement)
            .map((element) => [
              element.dataset.rewardId || '',
              {
                productId: Number(element.dataset.productId || 0),
                variantId: Number(element.dataset.variantId || 0),
              },
            ])
        );
        const subscriptionGiftRules = Array.from(
          this.querySelectorAll('[data-subscription-gift-rule]')
        )
          .filter((element) => element instanceof HTMLElement)
          .map((element) => ({
            marker: normalizedMarker(element.dataset.marker),
            productId: Number(element.dataset.productId || 0),
            parentProductId: Number(element.dataset.parentProductId || 0),
            parentSellingPlanId: normalizedShopifyId(element.dataset.parentSellingPlanId),
            parentQuantityDivisor: Math.max(
              1,
              Number(element.dataset.parentQuantityDivisor || 1)
            ),
            suppressesProductId: Number(element.dataset.suppressesProductId || 0),
          }))
          .filter((rule) => rule.marker);
        const subscriptionGiftMarkers = new Set(
          subscriptionGiftRules.map((rule) => rule.marker)
        );
        const subscriptionGiftLines = new Map();

        for (const item of cartItems) {
          const rewardId = item.properties?._lngvty_reward;
          if (rewardId) {
            const rewardLines = authoritativeRewardLines.get(String(rewardId)) || [];
            rewardLines.push({
              key: String(item.key || ''),
              quantity: Number(item.quantity || 0),
              productId: Number(item.product_id || 0),
              variantId: Number(item.variant_id || 0),
              finalLinePrice: Number(item.final_line_price || 0),
              label: String(item.properties?._lngvty_reward_label || ''),
              replacesPaid:
                String(item.properties?._lngvty_reward_replaces_paid || '')
                  .trim()
                  .toLowerCase() === 'true',
            });
            authoritativeRewardLines.set(String(rewardId), rewardLines);
          } else {
            const bundleMarker = normalizedMarker(item.properties?._bundle);
            if (subscriptionGiftMarkers.has(bundleMarker)) {
              const giftLines = subscriptionGiftLines.get(bundleMarker) || [];
              giftLines.push(item);
              subscriptionGiftLines.set(bundleMarker, giftLines);
              continue;
            }

            qualifyingTotal += Number(item.final_line_price || 0);
            if (!bundleMarker) {
              const productId = Number(item.product_id || 0);
              const productLines = authoritativeUnmarkedProductLines.get(productId) || [];
              productLines.push({
                key: String(item.key || ''),
                finalLinePrice: Number(item.final_line_price || 0),
              });
              authoritativeUnmarkedProductLines.set(productId, productLines);
              authoritativeUnmarkedVariantIds.add(Number(item.variant_id || 0));
            }
          }
        }

        for (const rule of subscriptionGiftRules) {
          const parentQuantity = cartItems.reduce((total, item) => {
            if (
              Number(item.product_id || 0) !== rule.parentProductId ||
              !item.selling_plan_allocation
            ) {
              return total;
            }
            const sellingPlanId = normalizedShopifyId(
              item.selling_plan_allocation?.selling_plan?.id
            );
            if (rule.parentSellingPlanId && sellingPlanId !== rule.parentSellingPlanId) {
              return total;
            }
            return total + Number(item.quantity || 0);
          }, 0);
          let remainingGiftQuantity = Math.floor(parentQuantity / rule.parentQuantityDivisor);

          const orderedGiftLines = [...(subscriptionGiftLines.get(rule.marker) || [])].sort(
            (first, second) =>
              Number(first.final_line_price || 0) - Number(second.final_line_price || 0)
          );
          for (const giftLine of orderedGiftLines) {
            const quantity = Number(giftLine.quantity || 0);
            const isCorrectGift =
              !giftLine.selling_plan_allocation &&
              Number(giftLine.product_id || 0) === rule.productId &&
              Number(giftLine.final_line_price || 0) === 0;
            const allowedQuantity = isCorrectGift
              ? Math.min(quantity, remainingGiftQuantity)
              : 0;
            if (giftLine.key && allowedQuantity !== quantity) {
              updates[String(giftLine.key)] = allowedQuantity;
            }
            remainingGiftQuantity -= allowedQuantity;
            if (
              allowedQuantity > 0 &&
              rule.suppressesProductId === subscriptionBottleProductId
            ) {
              subscriptionBottlePresent = true;
            }
            if (
              allowedQuantity > 0 &&
              rule.suppressesProductId === subscriptionLibraryProductId
            ) {
              subscriptionLibraryPresent = true;
            }
          }
        }

        for (const [rewardId, rewardLines] of authoritativeRewardLines) {
          const config = rewardConfigs.get(rewardId);
          const validLines = [];
          for (const rewardLine of rewardLines) {
            const matchesCurrentReward =
              config &&
              rewardLine.productId === config.productId &&
              rewardLine.variantId === config.variantId;
            if (matchesCurrentReward) {
              validLines.push(rewardLine);
              continue;
            }
            if (rewardLine.key) updates[rewardLine.key] = 0;
            if (
              rewardLine.replacesPaid &&
              rewardLine.variantId > 0 &&
              !authoritativeUnmarkedVariantIds.has(rewardLine.variantId)
            ) {
              additions.push({ id: rewardLine.variantId, quantity: 1 });
              authoritativeUnmarkedVariantIds.add(rewardLine.variantId);
            }
          }
          authoritativeRewardLines.set(rewardId, validLines);
        }
      }

      for (const element of this.querySelectorAll('[data-reward-id]')) {
        if (!(element instanceof HTMLElement)) continue;

        const rewardId = element.dataset.rewardId || '';
        const rewardLines = authoritative ? authoritativeRewardLines.get(rewardId) || [] : [];
        const threshold = Number(element.dataset.threshold || 0);
        const productId = Number(element.dataset.productId || 0);
        const variantId = Number(element.dataset.variantId || 0);
        const desiredLabel = element.dataset.label || 'Free reward';
        const unmarkedProductLines = authoritative
          ? authoritativeUnmarkedProductLines.get(productId) || []
          : [];
        const unmarkedProductTotal = unmarkedProductLines.reduce(
          (total, line) => total + line.finalLinePrice,
          0
        );
        const rewardQualifyingTotal = authoritative
          ? qualifyingTotal - unmarkedProductTotal
          : qualifyingTotal;
        const authoritativeLine =
          rewardLines.find(
            (line) =>
              line.variantId === variantId && line.label === desiredLabel && line.replacesPaid
          ) ||
          rewardLines.find(
            (line) => line.variantId === variantId && line.label === desiredLabel
          ) || rewardLines.find((line) => line.variantId === variantId) || rewardLines[0];
        const lineKey = authoritative ? authoritativeLine?.key || '' : element.dataset.lineKey || '';
        const lineQuantity = authoritative
          ? authoritativeLine?.quantity || 0
          : Number(element.dataset.lineQuantity || 0);
        const lineVariantId = authoritative
          ? authoritativeLine?.variantId || 0
          : Number(element.dataset.lineVariantId || 0);
        const lineLabel = authoritative
          ? authoritativeLine?.label || ''
          : element.dataset.lineLabel || '';
        const lineReplacesPaid = authoritativeLine?.replacesPaid === true;
        const isEnabled = element.dataset.enabled === 'true';
        const shouldAutoAdd = element.dataset.autoAdd === 'true';
        const isSuppressed =
          (element.dataset.suppressWhenSubscriptionBottle === 'true' && subscriptionBottlePresent) ||
          (element.dataset.suppressWhenSubscriptionLibrary === 'true' && subscriptionLibraryPresent);
        const rewardSignature = `${rewardId}:${productId}:${variantId}:${threshold}`;
        const rewardDiscountFailed =
          authoritative &&
          isEnabled &&
          shouldAutoAdd &&
          !isSuppressed &&
          rewardQualifyingTotal >= threshold &&
          rewardLines.some(
            (line) => line.variantId === variantId && line.finalLinePrice > 0
          );
        if (rewardDiscountFailed) globalThis[rewardFailureKey].add(rewardSignature);
        const isEligible =
          isEnabled &&
          shouldAutoAdd &&
          !isSuppressed &&
          !globalThis[rewardFailureKey].has(rewardSignature) &&
          productId > 0 &&
          variantId > 0 &&
          rewardQualifyingTotal >= threshold;
        const hasCorrectReward =
          Boolean(lineKey) && lineVariantId === variantId && lineLabel === desiredLabel;
        const rewardShouldRememberPaid = lineReplacesPaid || unmarkedProductLines.length > 0;
        const needsPaidOriginUpgrade =
          isEligible && hasCorrectReward && rewardShouldRememberPaid && !lineReplacesPaid;

        if (authoritative) {
          for (const rewardLine of rewardLines) {
            if (rewardLine.key && rewardLine.key !== lineKey) updates[rewardLine.key] = 0;
          }
        }

        if (lineKey && (!isEligible || !hasCorrectReward || needsPaidOriginUpgrade)) {
          updates[lineKey] = 0;
        }
        if (hasCorrectReward && !needsPaidOriginUpgrade && lineQuantity !== 1) {
          updates[lineKey] = 1;
        }

        if (isEligible) {
          for (const productLine of unmarkedProductLines) {
            if (productLine.key) updates[productLine.key] = 0;
          }
        }

        if (!isEligible && lineReplacesPaid && unmarkedProductLines.length === 0) {
          additions.push({ id: variantId, quantity: 1 });
        }

        if (isEligible && (!hasCorrectReward || needsPaidOriginUpgrade)) {
          const properties = {
            _lngvty_reward: rewardId,
            _lngvty_reward_label: desiredLabel,
          };
          if (rewardShouldRememberPaid) {
            properties._lngvty_reward_replaces_paid = 'true';
          }
          additions.push({
            id: variantId,
            quantity: 1,
            properties,
          });
        }
      }

      if (!Object.keys(updates).length && !additions.length) {
        this.dataset.syncState = 'settled';
        return;
      }

      const keepDrawerOpen = this.closest('dialog')?.open === true;
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : null))
        .filter(Boolean);
      const uniqueSectionIds = [...new Set(sectionIds)];
      const sectionRequest = uniqueSectionIds.length
        ? { sections: uniqueSectionIds.join(','), sections_url: window.location.pathname + ((window.Shopify?.theme?.role === 'unpublished' || window.Shopify?.theme?.role === 'development') ? '?preview_theme_id=' + window.Shopify.theme.id : '') }
        : {};
      let cart = null;
      let renderedSections = null;

      this.dataset.syncState = 'syncing';

      if (Object.keys(updates).length) {
        const updateResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates, ...sectionRequest }),
        });
        if (!updateResponse.ok) throw new Error('Unable to update cart rewards');
        cart = await updateResponse.json();
        renderedSections = cart.sections || null;
      }

      if (additions.length) {
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: additions, ...sectionRequest }),
        });
        if (!addResponse.ok) throw new Error('Unable to add cart rewards');
        const addResult = await addResponse.json();
        renderedSections = addResult.sections || renderedSections;

        const cartResponse = await fetch('/cart.js', {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (!cartResponse.ok) throw new Error('Unable to refresh cart rewards');
        cart = await cartResponse.json();
      }

      if (!cart) throw new Error('Unable to reconcile cart rewards');
      if (renderedSections) cart.sections = renderedSections;

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'lngvty-cart-reward',
            data: {
              source: 'lngvty-cart-reward',
              itemCount: Number(cart.item_count || 0),
              sections: cart.sections || {},
              openMiniCart: keepDrawerOpen && Boolean(document.querySelector('.cart-drawer__dialog[open]:not(.dialog-closing)')),
              rewardSync: true,
            },
          },
        })
      );
      this.dataset.syncState = 'complete';
    } catch (error) {
      this.dataset.syncState = 'error';
      console.error('LNGVTY cart reward synchronization failed.', error);
    } finally {
      globalThis[rewardSyncKey] = false;
    }
  }
}

if (!customElements.get('lngvty-cart-reward-manager')) {
  customElements.define('lngvty-cart-reward-manager', LngvtyCartRewardManager);
}

function scheduleAuthoritativeRewardReconciliation() {
  window.clearTimeout(globalThis[rewardReconcileTimerKey]);
  globalThis[rewardReconcileTimerKey] = window.setTimeout(() => {
    const manager = document.querySelector('lngvty-cart-reward-manager');
    if (manager instanceof LngvtyCartRewardManager) manager.reconcile({ authoritative: true });
  }, 0);
}

document.addEventListener('cart:update', (event) => {
  if (event.detail?.data?.source === 'lngvty-cart-reward') return;
  scheduleAuthoritativeRewardReconciliation();
});

document.addEventListener('cart:refresh', (event) => {
  if (event.detail?.rewardSync) return;
  scheduleAuthoritativeRewardReconciliation();
});

class LngvtyCartMotivator extends HTMLElement {
  static get observedAttributes() {
    return ['data-cart-total', 'data-progress'];
  }

  #progressTimer;
  #unlockTimers = [];
  #resizeObserver;
  #observedWidth = 0;

  connectedCallback() {
    this.#sync();
    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(([entry]) => {
        const width = entry?.contentRect.width || this.clientWidth;
        if (Math.abs(width - this.#observedWidth) < 0.5) return;
        this.#observedWidth = width;
        window.requestAnimationFrame(() => this.#fitMessage());
      });
      this.#resizeObserver.observe(this);
    }
    window.requestAnimationFrame(() => this.#fitMessage());
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    window.clearTimeout(this.#progressTimer);
    this.#unlockTimers.forEach((timer) => window.clearTimeout(timer));
    this.#unlockTimers = [];
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (!this.isConnected || oldValue === null || oldValue === newValue) return;
    window.requestAnimationFrame(() => this.#sync());
  }

  #sync() {
    const total = Number(this.dataset.cartTotal || 0);
    const progress = Number(this.dataset.progress || 0);
    const message = this.querySelector('.lngvty-cart-motivator__message')?.textContent?.replace(/\s+/g, ' ').trim() || '';
    const milestones = Array.from(this.querySelectorAll('[data-target]'));
    const finalTarget = milestones.reduce((highest, milestone) => Math.max(highest, Number(milestone.dataset.target || 0)), 0);
    const previous = globalThis[stateKey];

    if (!previous || !Number.isFinite(previous.total) || !Number.isFinite(previous.progress)) {
      globalThis[stateKey] = { total, progress, message };
      window.requestAnimationFrame(() => this.#fitMessage());
      return;
    }

    if (previous.total === total && previous.progress === progress && previous.message === message) {
      window.requestAnimationFrame(() => this.#fitMessage());
      return;
    }

    this.#animateProgress(previous.progress, progress);
    this.#animateMessage(message);
    this.classList.remove('is-progressing');
    this.getBoundingClientRect();
    this.classList.add('is-progressing');
    this.#spawnSparkles(progress);
    window.clearTimeout(this.#progressTimer);
    this.#progressTimer = window.setTimeout(() => this.classList.remove('is-progressing'), 760);

    milestones.forEach((milestone) => {
      if (!(milestone instanceof HTMLElement)) return;
      const target = Number(milestone.dataset.target || 0);
      const externallySatisfied = milestone.dataset.satisfied === 'true';
      const justUnlocked = !externallySatisfied && previous.total < target && total >= target;
      const justRelocked = !externallySatisfied && previous.total >= target && total < target;

      milestone.classList.toggle('is-achieved', externallySatisfied || total >= target);
      if (justUnlocked) this.#celebrate(milestone, target === finalTarget ? 95 : 70);
      if (justRelocked) this.#relock(milestone);
    });

    if (previous.total < finalTarget && total >= finalTarget) this.#grandFinale(milestones);
    globalThis[stateKey] = { total, progress, message };
    window.requestAnimationFrame(() => this.#fitMessage());
  }

  #fitMessage() {
    const element = this.querySelector('.lngvty-cart-motivator__message');
    if (!(element instanceof HTMLElement)) return;

    element.style.removeProperty('font-size');
    const availableWidth = element.clientWidth;
    const naturalWidth = element.scrollWidth;
    if (!availableWidth || naturalWidth <= availableWidth) return;

    const baseSize = Number.parseFloat(window.getComputedStyle(element).fontSize);
    if (!Number.isFinite(baseSize)) return;

    const fittedSize = Math.max(9, baseSize * (availableWidth / naturalWidth) * 0.98);
    element.style.fontSize = `${fittedSize}px`;
  }

  #animateProgress(previousProgress, progress) {
    const fill = this.querySelector('.lngvty-cart-motivator__fill');
    const comet = this.querySelector('.lngvty-cart-motivator__comet');

    if (fill instanceof HTMLElement) {
      fill.style.transition = 'none';
      fill.style.width = `${previousProgress}%`;
      fill.getBoundingClientRect();
      fill.style.removeProperty('transition');
      window.requestAnimationFrame(() => {
        fill.style.width = `${progress}%`;
      });
    }

    if (comet instanceof HTMLElement) {
      comet.style.transition = 'none';
      comet.style.left = `${previousProgress}%`;
      comet.getBoundingClientRect();
      comet.style.removeProperty('transition');
      window.requestAnimationFrame(() => {
        comet.style.left = `${progress}%`;
      });
    }
  }

  #animateMessage(text) {
    const element = this.querySelector('.lngvty-cart-motivator__message');
    if (!(element instanceof HTMLElement) || reduceMotion.matches || !text) return;

    element.setAttribute('aria-label', text);
    element.textContent = '';
    Array.from(text).forEach((character, index) => {
      const span = document.createElement('span');
      span.className = 'lngvty-cart-motivator__character';
      span.setAttribute('aria-hidden', 'true');
      span.style.setProperty('--lngvty-character-index', index);
      span.textContent = character === ' ' ? '\u00a0' : character;
      element.append(span);
    });
    window.requestAnimationFrame(() => this.#fitMessage());
  }

  #spawnSparkles(progress) {
    if (reduceMotion.matches) return;
    const bar = this.querySelector('.lngvty-cart-motivator__bar');
    if (!(bar instanceof HTMLElement)) return;

    for (let index = 0; index < 12; index += 1) {
      const sparkle = document.createElement('span');
      sparkle.className = 'lngvty-cart-motivator__sparkle';
      sparkle.style.setProperty('--lngvty-sparkle-x', `${Math.max(1, Math.min(99, progress + (Math.random() - 0.5) * 9))}%`);
      sparkle.style.setProperty('--lngvty-sparkle-y', `${49 + (Math.random() - 0.5) * 18}px`);
      sparkle.style.setProperty('--lngvty-sparkle-delay', `${Math.round(Math.random() * 120)}ms`);
      sparkle.style.setProperty('--lngvty-sparkle-size', `${3 + Math.round(Math.random() * 4)}px`);
      bar.append(sparkle);
      sparkle.addEventListener('animationend', () => sparkle.remove(), { once: true });
    }
  }

  #celebrate(milestone, count) {
    milestone.classList.remove('is-newly-achieved', 'is-relocking');
    milestone.getBoundingClientRect();
    milestone.classList.add('is-newly-achieved');
    this.#burstAt(milestone, count);
    const timer = window.setTimeout(() => milestone.classList.remove('is-newly-achieved'), 950);
    this.#unlockTimers.push(timer);
  }

  #relock(milestone) {
    milestone.classList.remove('is-newly-achieved', 'is-relocking');
    milestone.getBoundingClientRect();
    milestone.classList.add('is-relocking');
    const timer = window.setTimeout(() => milestone.classList.remove('is-relocking'), 450);
    this.#unlockTimers.push(timer);
  }

  #grandFinale(milestones) {
    const achieved = milestones.filter((milestone) => milestone.classList.contains('is-achieved'));
    achieved.forEach((milestone, index) => {
      const timer = window.setTimeout(() => this.#burstAt(milestone, 48), 100 + index * 90);
      this.#unlockTimers.push(timer);
    });
    this.classList.add('is-complete');
    const timer = window.setTimeout(() => this.classList.remove('is-complete'), 1200);
    this.#unlockTimers.push(timer);
  }

  #burstAt(milestone, count) {
    const icon = milestone.querySelector('.lngvty-cart-motivator__icon');
    if (!(icon instanceof HTMLElement)) return;
    const rect = icon.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    getConfetti()?.burst(rect.left + rect.width / 2, rect.top + rect.height / 2, count);
  }
}

if (!customElements.get('lngvty-cart-motivator')) {
  customElements.define('lngvty-cart-motivator', LngvtyCartMotivator);
}
